import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { RootState } from "../store/reducers";
import transactionApi from "../store/modules/transactionYear/transactionApi";
import { TransactionStatus } from "../store/repsaApi";
import { NO_YEARS_SELECTED } from "../ui-components/YearFilterSelect";

export { TransactionStatus };

export const PIPELINE_ROUTE = "pipeline";

export const isPipelineStatus = (status?: TransactionStatus | null) =>
  status === TransactionStatus.Lead || status === TransactionStatus.Active;

export const TRANSACTION_STATUS_OPTIONS = [
  { value: TransactionStatus.Lead, label: "Lead" },
  { value: TransactionStatus.Active, label: "Active" },
  { value: TransactionStatus.Transaction, label: "Transaction" },
];

export const transactionStatusLabel = (status?: TransactionStatus) =>
  TRANSACTION_STATUS_OPTIONS.find((o) => o.value === status)?.label ??
  "Transaction";

/** "Closing Date" for Transactions, "Potential Closing Date" for Lead/Active */
export const closingDateLabel = (status?: TransactionStatus | null) =>
  isPipelineStatus(status) ? "Potential Closing Date" : "Closing Date";

/** Potential Closing Date already in the past */
export const isOverdueEntry = (
  status?: TransactionStatus | null,
  date?: Dayjs | string | null,
) => isPipelineStatus(status) && !!date && dayjs(date).isBefore(dayjs(), "day");

/** The Pipeline feature can be turned off in Settings > Category Options */
export const usePipelineEnabled = () => {
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  return !hiddenFields?.some((field) => field.name === "pipeline");
};

/** Colour of the "+ Pipeline" dots (same as the GCI tracker) */
export const PIPELINE_DOT_COLOR = "#8e24aa";

interface ChartYear {
  id?: number;
  year?: number;
}

/** An analytics row with the flags that split it between the line and the dots */
interface AnalyticsRow {
  yearId?: number;
  year?: number;
  isPipeline?: boolean;
  isUnderContract?: boolean;
}

/** Year folders for analytics charts, and the Pipeline folder id when the feature is on */
export const useAnalyticsYears = () => {
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const pipelineEnabled = usePipelineEnabled();
  const { data: pipelineFolder } = usePipelineFolder(pipelineEnabled);
  return {
    years,
    pipelineYearId: pipelineEnabled ? pipelineFolder?.id : undefined,
  };
};

/** Axis entry for a year that has Pipeline entries but no year folder yet (e.g. 2027) */
export const isFolderlessYearId = (yearId?: number) =>
  yearId !== undefined && yearId < NO_YEARS_SELECTED;

/**
 * Splits analytics rows into the regular line ("actual") and the "+ Under Contracts" and
 * "+ Pipeline" dot sets, and works out the years on the X axis.
 * - The X axis is years only. Every Pipeline entry counts (the server already leaves out
 *   overdue ones, whose Potential Closing Date has passed), whatever years are selected: it
 *   goes to the year of its Potential Closing Date, taking that year folder's id, or an axis
 *   entry of its own for a year with no folder (e.g. 2027). Such years join the axis.
 * - With "Under Contracts" checked the API returns the Under Contract transactions of every
 *   year folder, whatever years are selected; their years join the axis too.
 * - On every time axis (years, quarters or months), Pipeline and Under Contracts are "+"
 *   dot series on top of the line, one per period, never an extra axis entry. Under Contract
 *   transactions leave the line only when "Under Contracts" is checked.
 * - A year on the axis only for its dots (not selected, or no folder) has no line; with no
 *   year folder selected there is no line at all.
 */
export function splitAnalyticsRows<T extends AnalyticsRow>(
  rows: T[] | null | undefined,
  options: {
    years: ChartYear[];
    selectedYearIds?: number[];
    pipelineYearId?: number;
    includeUnderContracts?: boolean;
  },
) {
  const { pipelineYearId, includeUnderContracts = false } = options;
  const data = rows ?? [];
  const selected = options.selectedYearIds ?? [];
  const years = options.years.filter((y) => y.id !== pipelineYearId);
  const includePipeline =
    pipelineYearId !== undefined && selected.includes(pipelineYearId);

  const allYears = selected.length === 0;
  const realSelected = selected.filter(
    (id) => id !== pipelineYearId && id !== NO_YEARS_SELECTED,
  );
  const noRealYears = !allYears && realSelected.length === 0;
  // Years whose own Transactions are fetched, so they have a line
  const lineYearIds = allYears ? years.map((y) => y.id!) : realSelected;

  const extraYears: ChartYear[] = [];
  const pipeline: T[] = [];
  if (includePipeline) {
    data
      .filter((row) => row.isPipeline && row.year)
      .forEach((row) => {
        const folder = years.find((y) => y.year === row.year);
        const id = folder ? folder.id! : -row.year!;
        if (!folder && !extraYears.some((y) => y.id === id)) {
          extraYears.push({ id, year: row.year });
        }
        pipeline.push({ ...row, yearId: id });
      });
  }

  const underContract = includeUnderContracts
    ? data.filter((row) => !row.isPipeline && row.isUnderContract)
    : [];

  const chartYears = [...years, ...extraYears];
  const axisIds = Array.from(
    new Set([
      ...lineYearIds,
      ...pipeline.map((row) => row.yearId!),
      ...underContract.map((row) => row.yearId!),
    ]),
  );
  const yearOf = (id: number) => chartYears.find((y) => y.id === id)?.year ?? 0;
  const periodYearIds = axisIds.sort((a, b) => yearOf(a) - yearOf(b));

  return {
    /** Real year folders plus axis entries for later years without a folder */
    chartYears,
    /** Year ids on the X axis, oldest first */
    periodYearIds,
    /** One year on the axis by selection (quarters or months of that year) */
    isSingleYear: !allYears && periodYearIds.length === 1,
    /** A year on the axis only for its dots (not selected, or no folder) has no line */
    isDotsOnlyYear: (yearId?: number) =>
      yearId === undefined || !lineYearIds.includes(yearId),
    // Whether there are "+ Pipeline" / "+ Under Contracts" dots to draw (and list in the legend)
    includePipeline: pipeline.length > 0,
    includeUnderContracts: underContract.length > 0,
    // With no year folder selected there is no line, only the Pipeline / Under Contracts dots
    actual: noRealYears
      ? []
      : data.filter(
          (row) =>
            !row.isPipeline && !(includeUnderContracts && row.isUnderContract),
        ),
    underContract,
    pipeline,
  };
}

/** The user's Pipeline folder (its year id, entry and overdue counts) */
export const usePipelineFolder = (enabled: boolean = true) => {
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  return useQuery({
    queryKey: ["pipelineFolder", selectedCoAgentIds],
    queryFn: () => transactionApi.getPipelineFolder(),
    enabled,
  });
};
